import React, { useRef } from 'react';
import findIndex from 'lodash-es/findIndex';
import { Menu } from '@teetsh/app/src/components/Menu';
import {
  EllipsisVerticalIcon as DotsVertical,
  PlusIcon as Plus,
  TrashIcon as Trash,
} from '@heroicons/react/24/outline';
import { DuplicateIcon } from 'components/Icons/icons/Duplicate';
import {
  removeItemAtIndex,
  replaceItemAtIndex,
} from '@teetsh/app/src/utils/listMethods';
import tw from 'twin.macro';
import { DomaineSocleCommunPositionnement } from '@teetsh/app/src/containers/Livrets/types/livret';
import Buttons from '@teetsh/app/src/components/Form/ButtonsRadio';
import { useTranslation } from 'react-i18next';
import { useDuplicateSocle } from 'containers/Livrets/queries/useDuplicateSocle';

interface Props {
  value: DomaineSocleCommunPositionnement[];
  livretEvalId: string;
  livretEleveId: string;
  onChange?: (p: DomaineSocleCommunPositionnement[]) => void;
  print?: boolean;
}

interface DomaineSocleCommunNomenclature {
  libelle: string;
  code: string;
}

const allDomaines: DomaineSocleCommunNomenclature[] = [
  {
    code: 'CPD_FRA',
    libelle:
      "Comprendre, s'exprimer en utilisant la langue française à l'oral et à l'écrit",
  },
  {
    code: 'CPD_ETR',
    libelle:
      "Comprendre, s'exprimer en utilisant une langue étrangère et, le cas échéant, une langue régionale",
  },
  {
    code: 'CPD_SCI',
    libelle:
      "Comprendre, s'exprimer en utilisant les langages mathématiques, scientifiques et informatiques",
  },
  {
    code: 'CPD_ART',
    libelle:
      "Comprendre, s'exprimer en utilisant les langages des arts et du corps",
  },
  { code: 'MET_APP', libelle: 'Les méthodes et outils pour apprendre' },
  { code: 'FRM_CIT', libelle: 'La formation de la personne et du citoyen' },
  {
    code: 'SYS_NAT',
    libelle: 'Les systèmes naturels et les systèmes techniques',
  },
  {
    code: 'REP_MND',
    libelle: "Les représentations du monde et l'activité humaine",
  },
];

function getRemainingSocles(
  domaines: DomaineSocleCommunPositionnement[],
): DomaineSocleCommunNomenclature[] {
  return allDomaines.filter(d => !domaines.map(dd => dd.code).includes(d.code));
}

function positionToPositionString(
  position: 1 | 2 | 3 | 4,
): '1' | '2' | '3' | '4' {
  switch (position) {
    case 1:
      return '1';
    case 2:
      return '2';
    case 3:
      return '3';
    case 4:
      return '4';
  }
}

export function SocleCommunDomaines(props: Props) {
  const { value, onChange, print } = props;
  const remainingDomaines = getRemainingSocles(value);
  const duplicateSocle = useDuplicateSocle({
    livretEleveId: props.livretEleveId,
    livretEvalId: props.livretEvalId,
  });
  const { t } = useTranslation(['carnet', 'common']);
  const textareaRef = useRef();
  return (
    <div>
      {value.map(c => {
        const matchingNomenclature = allDomaines.find(
          nom => nom.code === c.code,
        );
        const index = findIndex(value, v => v.code === c.code);
        return (
          <div key={c.code} tw="relative mb-1">
            <div tw="flex justify-between items-baseline space-x-4">
              <div css={[!print && tw`mb-0 text-sm`]}>
                {matchingNomenclature?.libelle}
              </div>
              <div tw="flex shrink-0">
                {print ? (
                  <div>
                    {t(
                      `carnet:livret.maitrise.full.${positionToPositionString(
                        c.positionnement,
                      )}`,
                    )}
                  </div>
                ) : (
                  <Buttons<1 | 2 | 3 | 4>
                    withoutMargin
                    canBeEmpty
                    small
                    onChange={positionnement =>
                      onChange &&
                      onChange(
                        replaceItemAtIndex(
                          value,
                          { ...c, positionnement },
                          index,
                        ),
                      )
                    }
                    options={[
                      {
                        value: 1,
                        label: t('carnet:livret.maitrise.insuffisante'),
                      },
                      {
                        value: 2,
                        label: t('carnet:livret.maitrise.fragile'),
                      },
                      {
                        value: 3,
                        label: t('carnet:livret.maitrise.satisfaisante'),
                      },
                      {
                        value: 4,
                        label: t('carnet:livret.maitrise.tresbonne'),
                      },
                    ]}
                    value={c.positionnement}
                  />
                )}
                {!print && onChange && (
                  <Menu>
                    <Menu.Button
                      minimal
                      tw="text-muted font-medium"
                      small
                      icon={DotsVertical}
                    />
                    <Menu.Items size="auto">
                      <Menu.Item
                        tw="whitespace-nowrap"
                        onClick={() => duplicateSocle(c.code)}
                        icon={DuplicateIcon as any}
                        text={t('carnet:livret.copyOnOtherStudents')}
                      />
                      <Menu.Item
                        tw="whitespace-nowrap"
                        onClick={() =>
                          onChange(removeItemAtIndex(value, index))
                        }
                        icon={Trash}
                        intent="danger"
                        text={t('common:buttons.delete')}
                      />
                    </Menu.Items>
                  </Menu>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {!print && onChange && (
        <div css={[tw`text-center`, value.length > 0 && tw`mb-4`]}>
          <Menu large={value.length === 0}>
            <Menu.Button
              icon={Plus}
              fill
              extralarge={value.length === 0}
              minimal={value.length > 0}
            >
              {t('carnet:livret.addComposantesSocleCommun')}
            </Menu.Button>
            <Menu.Items fill limitHeight size="auto">
              {remainingDomaines.map(c => (
                <Menu.Item
                  key={c.code}
                  onClick={() => {
                    onChange([...value, { code: c.code, positionnement: 4 }]);
                    setTimeout(() => {
                      if (textareaRef?.current) {
                        (textareaRef.current as any)?.focus();
                      }
                    }, 100);
                  }}
                  text={c.libelle}
                />
              ))}
            </Menu.Items>
          </Menu>
        </div>
      )}
    </div>
  );
}
